'use client';

import * as rrd from 'react-router-dom';
import { useEffect, useState } from 'react';

import { Meta } from './segments';

import { supportConfig } from '@/config';
import { supportMeta, blurActiveElement } from '@/utils';

const { Link, useLocation } = rrd.default ?? rrd;

const copyToClipboard = async (text) => {
  blurActiveElement();

  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

// Shorten address for display (copy still uses full value)
const shortenAddress = (addr, start = 6, end = 4) => {
  if (!addr) return '';

  return `${addr.slice(0, start)}…${addr.slice(-end)}`;
};

const Row = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);

  if (!value) return null;

  const displayValue = revealed ? value : shortenAddress(value);

  return (
    <div className="border rounded p-3 mb-3">
      <div className="d-flex flex-column flex-md-row gap-2 gap-md-3">
        <div className="flex-grow-1">
          <div className="fw-semibold">{label}</div>

          <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
            <span
              style={{ wordBreak: 'break-all' }}
              className="text-muted font-monospace small"
            >
              {displayValue}
            </span>

            <button
              type="button"
              onClick={() => setRevealed((v) => !v)}
              className="btn btn-link p-0 small text-muted text-decoration-none"
            >
              {revealed ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        <button
          type="button"
          disabled={copied}
          style={{ maxWidth: '90px' }}
          title={copied ? 'Address copied' : 'Copy address'}
          aria-label={copied ? 'Address copied' : 'Copy address'}
          className="btn btn-sm btn-outline-custom btn-outline-accent btn-sm w-100 align-self-start"
          onClick={async () => {
            const ok = await copyToClipboard(value);
            if (!ok) return;

            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
          <i
            aria-hidden="true"
            className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'} ms-1`}
          />
        </button>
      </div>
    </div>
  );
};

const Support = () => {
  const {
    bmac,
    stripe,
    hasCrypto,
    hasSupport,
    crypto: { btc, eth, usdcSol },
  } = supportConfig;

  const { search } = useLocation();
  const [donationSuccess, setDonationSuccess] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const success = params.get('success') === '1';

    if (success) {
      setDonationSuccess(true);

      // Clean URL so refresh/back doesn’t re-trigger thanks state
      window.history.replaceState({}, '', window.location.pathname);
    } else {
      setDonationSuccess(false);
    }
  }, [search]);

  const hasBmacAndStripe = Boolean(bmac && stripe);

  return (
    <>
      <Meta {...supportMeta} />

      {donationSuccess ? (
        <main
          className="container py-4 d-flex align-items-center"
          style={{ minHeight: 'calc(100vh - 410px)' }}
        >
          <div className="row justify-content-center text-center w-100">
            <div className="col-md-8 col-lg-6">
              <div className="mb-3">
                <span className="display-3 fw-bold text-accent">Thank you</span>
              </div>

              <h1 className="mb-3">Your support means a lot</h1>

              <p className="text-muted mb-4">
                We truly appreciate you supporting Convert Playlist.
              </p>

              <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
                <Link to="/" className="btn btn-accent px-4 py-2">
                  New Conversion
                </Link>

                <Link to="/" className="btn btn-outline-secondary px-4 py-2">
                  Go Home
                </Link>
              </div>

              <div className="mt-4">
                {/*<h6 className="mb-2">What’s next?</h6>*/}

                <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
                  <a href="/" className="text-decoration-none">
                    Home
                  </a>
                  <Link to="/privacy-policy" className="text-decoration-none">
                    Privacy
                  </Link>
                  <Link to="/terms-of-service" className="text-decoration-none">
                    Terms
                  </Link>
                  <Link to="/cookie-policy" className="text-decoration-none">
                    Cookies
                  </Link>
                  <Link to="/support" className="text-decoration-none">
                    Support
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </main>
      ) : (
        <main
          className="container py-4"
          style={{ minHeight: 'calc(100vh - 410px)' }}
        >
          <div className="text-center mb-4">
            <h1 className="mb-2">Support Convert Playlist</h1>
            <p className="text-muted m-0">
              Your support helps cover hosting, maintenance, and ongoing
              development.
            </p>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-8">
              {!hasSupport ? (
                <div className="border rounded p-3 text-center">
                  <p className="text-muted m-0">
                    Support options are temporarily unavailable.
                    <br />
                    You can still use Convert Playlist freely — thank you for
                    being here.
                  </p>
                </div>
              ) : (
                <>
                  {/* Support Overview */}
                  <div className="border rounded p-4 mb-4">
                    <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-3">
                      <div>
                        <h2 className="h5 mb-1">Support options</h2>
                      </div>
                    </div>

                    {/* Primary Methods */}
                    <>
                      {(stripe || bmac) && (
                        <>
                          <div className="row g-3">
                            {stripe && (
                              <div
                                className={`col-12 ${hasBmacAndStripe ? 'col-md-6' : ''}`}
                              >
                                <a
                                  href={stripe}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={blurActiveElement}
                                  className="btn btn-outline-custom btn-outline-accent w-100 d-flex align-items-center justify-content-between"
                                >
                                  <span className="d-flex align-items-center gap-2">
                                    <i
                                      aria-hidden="true"
                                      className="fa-solid fa-credit-card"
                                    />
                                    <span className="fw-semibold">
                                      Card & Wallets
                                    </span>
                                  </span>

                                  <i
                                    aria-hidden="true"
                                    className="fa-solid fa-arrow-up-right-from-square"
                                  />
                                </a>
                              </div>
                            )}

                            {bmac && (
                              <div
                                className={`col-12 ${hasBmacAndStripe ? 'col-md-6' : ''}`}
                              >
                                <a
                                  href={bmac}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={blurActiveElement}
                                  className="btn btn-outline-custom btn-outline-accent w-100 d-flex align-items-center justify-content-between"
                                >
                                  <span className="d-flex align-items-center gap-2">
                                    <i
                                      aria-hidden="true"
                                      className="fa-solid fa-mug-saucer"
                                    />
                                    <span className="fw-semibold">
                                      Buy Me a Coffee
                                    </span>
                                  </span>

                                  <i
                                    aria-hidden="true"
                                    className="fa-solid fa-arrow-up-right-from-square"
                                  />
                                </a>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </>

                    {/* Divider */}
                    {hasCrypto && <hr className="my-4" />}

                    {/* Crypto */}
                    {hasCrypto && (
                      <div>
                        <div className="fw-semibold mb-1">
                          Crypto
                          <span className="text-muted fw-normal ms-2 small">
                            (copy addresses)
                          </span>
                        </div>

                        <p className="text-muted mb-3">
                          Send only to the network shown. Double-check before
                          sending — crypto transfers are irreversible.
                        </p>

                        <Row
                          label="USDC (or SOL) — Solana network"
                          value={usdcSol}
                        />
                        <Row label="BTC — Bitcoin network" value={btc} />
                        <Row
                          label="ETH (or ERC-20 tokens) — Ethereum mainnet"
                          value={eth}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      )}
    </>
  );
};

export default Support;
